/**
 * El guion del asistente: qué pregunta, en qué orden y cuándo termina.
 *
 * ⚠ **Aparte del componente a propósito.** Así el orden de las preguntas y las
 * reglas de «esto ya se puede enviar» se prueban sin montar nada de React, y
 * cambiar una frase no obliga a releer el render entero.
 *
 * ⚠ **El guion es fijo y eso es una decisión, no una limitación.** Un bot que
 * repregunta libremente saca relatos distintos de la misma falla, y dev acaba
 * leyendo cinco formas de contar lo mismo. Cuatro preguntas iguales para todo el
 * mundo producen reportes comparables: es lo que permite ver que tres personas
 * reportaron el MISMO fallo.
 */

export type Paso = "VISTA" | "INTENTO" | "RESULTADO" | "TITULO" | "EVIDENCIA" | "REVISION"

export const PASOS: Paso[] = ["VISTA", "INTENTO", "RESULTADO", "TITULO", "EVIDENCIA", "REVISION"]

/** Lo que el asistente dice en cada paso. Segunda persona y sin jerga. */
export const PREGUNTA: Record<Paso, string> = {
  VISTA:
    "Vamos a reportar una falla. Primero: ¿el problema te ocurrió en esta pantalla, " +
    "o fue en otra?",
  INTENTO: "¿Qué estabas intentando hacer? Cuéntamelo como se lo contarías a un compañero.",
  RESULTADO: "¿Y qué pasó en vez de eso?",
  TITULO: "Ponle un título corto, para que en la lista de desarrollo se reconozca de un vistazo.",
  EVIDENCIA:
    "Esto es lo que el navegador registró. Marca lo que tenga que ver con tu problema — " +
    "si no reconoces nada, no pasa nada, déjalo como está.",
  REVISION: "Esto es todo lo que voy a enviar. Míralo antes, que va a desarrollo tal cual.",
}

export const LARGO_MINIMO = 5
export const LARGO_TITULO_MINIMO = 5

export type Borrador = {
  vista: string
  queIntentaba: string
  quePaso: string
  titulo: string
  /** Índices de las señales que quien reporta marcó como suyas. */
  marcadas: number[]
  captura: File | null
}

export function borradorVacio(vistaActual: string): Borrador {
  return {
    vista: vistaActual,
    queIntentaba: "",
    quePaso: "",
    titulo: "",
    marcadas: [],
    captura: null,
  }
}

/**
 * ¿Se puede pasar del paso actual?
 *
 * ⚠ **`EVIDENCIA` no exige marcar nada.** Un fallo que no deja rastro en la red es
 * de los que más importan —un total que no cuadra, un botón que no responde— y
 * obligar a señalar un error para poder seguir dejaría fuera justo esa clase.
 */
export function puedeAvanzar(paso: Paso, b: Borrador): boolean {
  switch (paso) {
    case "VISTA":
      return b.vista.trim() !== ""
    case "INTENTO":
      return b.queIntentaba.trim().length >= LARGO_MINIMO
    case "RESULTADO":
      return b.quePaso.trim().length >= LARGO_MINIMO
    case "TITULO":
      return b.titulo.trim().length >= LARGO_TITULO_MINIMO
    case "EVIDENCIA":
      return true
    case "REVISION":
      return true
  }
}

export function siguiente(paso: Paso): Paso {
  const i = PASOS.indexOf(paso)
  return PASOS[Math.min(i + 1, PASOS.length - 1)] as Paso
}

export function anterior(paso: Paso): Paso {
  const i = PASOS.indexOf(paso)
  return PASOS[Math.max(i - 1, 0)] as Paso
}

/**
 * Una señal, en una línea que se pueda leer sin ser programador.
 *
 * ⚠ **Se enseña tal cual se va a enviar.** Resumirla aquí y mandar otra cosa sería
 * pedir permiso sobre una versión bonita de lo que de verdad viaja.
 */
export function resumirSenal(s: {
  clase: string
  estado?: number
  codigo?: string
  ruta?: string
  nombre?: string
  mensaje?: string
}): string {
  if (s.clase === "EXCEPCION" || s.clase === "RENDER") {
    const donde = s.clase === "RENDER" ? "La pantalla se cayó" : "Error en el navegador"
    return `${donde}: ${s.nombre ?? "Error"} — ${s.mensaje ?? "sin mensaje"}`
  }
  if (s.clase === "RED") {
    return `No hubo respuesta al pedir ${s.ruta ?? "algo al servidor"}`
  }
  const que = s.codigo ?? `estado ${s.estado ?? "?"}`
  return `El servidor respondió «${que}» en ${s.ruta ?? "una petición"}`
}
